import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    Bars3Icon,
    CheckIcon,
    DocumentCheckIcon,
    InformationCircleIcon,
    PlusIcon,
    ShieldCheckIcon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import QuestionInput from "./QuestionInput";
import {
    getQuestionnaire,
    saveQuestionnaire,
    saveQuestion,
    deleteQuestion,
    reorderQuestions,
    uploadConsentDocument,
    uploadEthicsDocument,
} from "../../Requests/QuestionnaireRequests";
import { questionnaireError } from "./questionnaireErrors";
import ParticleLoader from "../ParticleLoader";

/**
 * Three-pane questionnaire builder: step rail on the left, live question
 * preview in the middle, settings for the current step on the right.
 *
 * The centre pane edits the question exactly as the respondent will see it —
 * the same QuestionInput the public page renders, in a disabled state — so the
 * preview cannot drift from the real thing.
 */

const QUESTION_TYPES = [
    { value: "voice", labelKey: "Questionnaire.TypeVoice", fallback: "Voice answer" },
    { value: "textarea", labelKey: "Questionnaire.TypeLongText", fallback: "Long text" },
    { value: "text", labelKey: "Questionnaire.TypeShortText", fallback: "Short text" },
    { value: "multiple_choice", labelKey: "Questionnaire.TypeChoice", fallback: "Multiple choice" },
    { value: "multiple_select", labelKey: "Questionnaire.TypeMultiSelect", fallback: "Multiple select" },
    { value: "rating", labelKey: "Questionnaire.TypeRating", fallback: "Rating scale" },
    { value: "yes_no", labelKey: "Questionnaire.TypeYesNo", fallback: "Yes / No" },
    { value: "date", labelKey: "Questionnaire.TypeDate", fallback: "Date" },
];

const CHOICE_TYPES = ["multiple_choice", "multiple_select"];

/** Mirrors i18n's supportedLngs and Questionnaire::LANGUAGES on the backend. */
const LANGUAGES = [
    { value: "pt", labelKey: "Questionnaire.LanguagePt", fallback: "Portuguese" },
    { value: "en", labelKey: "Questionnaire.LanguageEn", fallback: "English" },
    { value: "es", labelKey: "Questionnaire.LanguageEs", fallback: "Spanish" },
];

const STEP_BASIC = "basic";
const STEP_CONSENT = "consent";

function typeLabel(t, type) {
    const match = QUESTION_TYPES.find((entry) => entry.value === type);
    return match ? t(match.labelKey, match.fallback) : type;
}

/** One draggable card in the left rail. */
function RailQuestion({ question, index, active, onSelect, t }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: question.uuid });

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            className={`rounded-lg transition-colors ${isDragging ? "opacity-60" : ""} ${
                active ? "bg-black text-white" : "bg-white hover:bg-gray-100"
            }`}
        >
            <div className="flex items-center gap-3 p-3">
                <button
                    type="button"
                    onClick={() => onSelect(index)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                    <span
                        className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-sm font-medium ${
                            active ? "bg-white text-black" : "bg-gray-200 text-gray-700"
                        }`}
                    >
                        {question.question_text?.trim() ? <CheckIcon className="h-4 w-4" /> : index + 3}
                    </span>
                    <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">
                            {question.question_text?.trim() ||
                                `${t("Questionnaire.QuestionText", "Question")} ${index + 1}`}
                        </span>
                        <span className={`block text-xs ${active ? "text-gray-300" : "text-gray-500"}`}>
                            {typeLabel(t, question.type)}
                        </span>
                    </span>
                </button>
                <button
                    type="button"
                    {...attributes}
                    {...listeners}
                    className={`flex-none cursor-grab p-1 ${active ? "text-gray-300" : "text-gray-400"}`}
                    title={t("Questionnaire.Reorder", "Drag to reorder")}
                >
                    <Bars3Icon className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

/** A step card in the left rail that is not a question (Basic info, Consent). */
function RailStep({ number, title, subtitle, active, done, onSelect }) {
    return (
        <button
            type="button"
            onClick={onSelect}
            className={`w-full rounded-lg p-3 text-left transition-colors ${
                active ? "bg-black text-white" : "bg-white hover:bg-gray-100"
            }`}
        >
            <div className="flex items-center gap-3">
                <span
                    className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-sm font-medium ${
                        active ? "bg-white text-black" : "bg-gray-200 text-gray-700"
                    }`}
                >
                    {done ? <CheckIcon className="h-4 w-4" /> : number}
                </span>
                <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{title}</span>
                    <span className={`block text-xs ${active ? "text-gray-300" : "text-gray-500"}`}>
                        {subtitle}
                    </span>
                </span>
            </div>
        </button>
    );
}

export default function QuestionnaireBuilderModal({ projectUuid, questionnaireUuid, onClose, onSaved }) {
    const { t } = useTranslation();
    const [questionnaire, setQuestionnaire] = useState(null);
    const [step, setStep] = useState(STEP_BASIC);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [saving, setSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [error, setError] = useState(null);

    const saveTimer = useRef(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const reload = useCallback(
        () => getQuestionnaire(projectUuid, questionnaireUuid).then((res) => {
            setQuestionnaire(res.data);
            return res.data;
        }),
        [projectUuid, questionnaireUuid]
    );

    useEffect(() => {
        reload();
    }, [reload]);

    const questions = useMemo(() => questionnaire?.questions || [], [questionnaire]);
    const currentQuestion = step === "question" ? questions[questionIndex] : null;

    /* ------------------------------------------------------------ saving */

    const persistSettings = useCallback(async (data) => {
        setSaving(true);
        setError(null);
        try {
            await saveQuestionnaire(projectUuid, {
                uuid: data.uuid,
                title: data.title,
                description: data.description,
                language: data.language,
                welcome_message: data.welcome_message,
                thank_you_message: data.thank_you_message,
                consent_content: data.consent_content,
                ethics_content: data.ethics_content,
                collect_email: data.collect_email,
                allow_multiple_responses: data.allow_multiple_responses,
                record_screen: data.record_screen,
                record_camera: data.record_camera,
            });
            setLastSaved(new Date());
            onSaved?.();
        } catch (e) {
            setError(questionnaireError(e, t));
        } finally {
            setSaving(false);
        }
    }, [projectUuid, onSaved, t]);

    const persistQuestion = useCallback(async (question) => {
        setSaving(true);
        setError(null);
        try {
            await saveQuestion(projectUuid, questionnaireUuid, {
                uuid: question.uuid,
                question_text: question.question_text || "",
                description: question.description || "",
                type: question.type,
                required: !!question.required,
                use_as_subject_property: !!question.use_as_subject_property,
                options: CHOICE_TYPES.includes(question.type)
                    ? (question.options || []).filter((option) => option && option.trim())
                    : null,
                min_value: question.type === "rating" ? question.min_value ?? 1 : null,
                max_value: question.type === "rating" ? question.max_value ?? 5 : null,
            });
            setLastSaved(new Date());
            onSaved?.();
        } catch (e) {
            setError(questionnaireError(e, t));
        } finally {
            setSaving(false);
        }
    }, [projectUuid, questionnaireUuid, onSaved, t]);

    /**
     * Debounced autosave, mirroring the "Saved HH:MM:SS" affordance: edits in
     * the centre pane persist on their own so the builder never loses work to a
     * closed tab.
     */
    const scheduleSave = useCallback((kind, payload) => {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            if (kind === "settings") persistSettings(payload);
            else persistQuestion(payload);
        }, 900);
    }, [persistSettings, persistQuestion]);

    useEffect(() => () => clearTimeout(saveTimer.current), []);

    /* ----------------------------------------------------------- mutators */

    const patchQuestionnaire = (changes) => {
        setQuestionnaire((prev) => {
            const next = { ...prev, ...changes };
            scheduleSave("settings", next);
            return next;
        });
    };

    const patchQuestion = (changes) => {
        setQuestionnaire((prev) => {
            const list = [...prev.questions];
            const updated = { ...list[questionIndex], ...changes };
            list[questionIndex] = updated;
            scheduleSave("question", updated);
            return { ...prev, questions: list };
        });
    };

    const addQuestion = async () => {
        setSaving(true);
        try {
            // Created server-side straight away so it has a uuid to autosave against.
            await saveQuestion(projectUuid, questionnaireUuid, {
                question_text: "",
                type: "textarea",
                required: true,
                options: null,
            });
            const fresh = await reload();
            setStep("question");
            setQuestionIndex(fresh.questions.length - 1);
            setLastSaved(new Date());
            onSaved?.();
        } finally {
            setSaving(false);
        }
    };

    const removeQuestion = async () => {
        if (!currentQuestion) return;
        setSaving(true);
        try {
            await deleteQuestion(projectUuid, questionnaireUuid, currentQuestion.uuid);
            const fresh = await reload();
            setQuestionIndex(0);
            setStep(fresh.questions.length ? "question" : STEP_BASIC);
            onSaved?.();
        } finally {
            setSaving(false);
        }
    };

    const handleDragEnd = async ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const from = questions.findIndex((q) => q.uuid === active.id);
        const to = questions.findIndex((q) => q.uuid === over.id);
        const reordered = arrayMove(questions, from, to);
        setQuestionnaire((prev) => ({ ...prev, questions: reordered }));
        if (currentQuestion) {
            setQuestionIndex(reordered.findIndex((q) => q.uuid === currentQuestion.uuid));
        }
        await reorderQuestions(projectUuid, questionnaireUuid, reordered.map((q) => q.uuid));
        setLastSaved(new Date());
        onSaved?.();
    };

    const handleDocumentUpload = (upload) => async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setSaving(true);
        try {
            await upload(projectUuid, questionnaireUuid, file);
            await reload();
            setLastSaved(new Date());
            onSaved?.();
        } catch (e) {
            setError(questionnaireError(e, t, "Generic.UploadFailed"));
        } finally {
            setSaving(false);
        }
    };

    if (!questionnaire) return null;

    const field =
        "w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black";
    const hasConsent = !!(questionnaire.consent_content?.trim() || questionnaire.consent_document_name);
    const hasEthics = !!(questionnaire.ethics_content?.trim() || questionnaire.ethics_document_name);

    const headerSubtitle =
        step === STEP_BASIC
            ? t("Questionnaire.BasicInformation", "Basic information")
            : step === STEP_CONSENT
                ? t("Questionnaire.Consent", "Informed consent")
                : t("Questionnaire.StepQuestionOf", "Question {{current}} of {{total}}", {
                    current: questionIndex + 1,
                    total: questions.length,
                });

    return (
        <Dialog open onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
            <div className="fixed inset-0">
                <Dialog.Panel className="flex h-full w-full flex-col bg-white">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b p-6">
                        <div>
                            <Dialog.Title className="text-xl font-semibold text-gray-900">
                                {t("Questionnaire.EditTitle", "Edit questionnaire")}
                            </Dialog.Title>
                            <div className="mt-1 flex items-center gap-4">
                                <span className="text-sm text-gray-600">{headerSubtitle}</span>
                                {saving && (
                                    <span className="flex items-center gap-2 text-sm text-amber-500">
                                        <ParticleLoader size={14} tone="current" />
                                        {t("Questionnaire.Saving", "Saving…")}
                                    </span>
                                )}
                                {lastSaved && !saving && (
                                    <span className="text-sm text-green-600">
                                        {t("Questionnaire.Saved", "Saved")} {lastSaved.toLocaleTimeString()}
                                    </span>
                                )}
                                {error && <span className="text-sm text-red-600">{error}</span>}
                            </div>
                        </div>
                        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="flex flex-1 overflow-hidden">
                        {/* Left — step rail */}
                        <div className="w-80 flex-none overflow-y-auto border-r bg-gray-50 p-6">
                            <div className="space-y-4">
                                <RailStep
                                    number="1"
                                    title={t("Questionnaire.StepBasicInfo", "Basic info")}
                                    subtitle={t("Questionnaire.StepBasicInfoSub", "Title, settings")}
                                    active={step === STEP_BASIC}
                                    done={!!questionnaire.title?.trim()}
                                    onSelect={() => setStep(STEP_BASIC)}
                                />

                                {/* Consent is a first-class step: a questionnaire cannot be
                                    published without it, so it should not be buried. */}
                                <RailStep
                                    number="2"
                                    title={t("Questionnaire.Consent", "Informed consent")}
                                    subtitle={
                                        hasConsent
                                            ? t("Questionnaire.ConsentReady", "Ready to sign")
                                            : t("Questionnaire.ConsentRequired", "Required")
                                    }
                                    active={step === STEP_CONSENT}
                                    done={hasConsent}
                                    onSelect={() => setStep(STEP_CONSENT)}
                                />

                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={questions.map((q) => q.uuid)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div className="space-y-4">
                                            {questions.map((question, index) => (
                                                <RailQuestion
                                                    key={question.uuid}
                                                    question={question}
                                                    index={index}
                                                    active={step === "question" && questionIndex === index}
                                                    onSelect={(i) => {
                                                        setStep("question");
                                                        setQuestionIndex(i);
                                                    }}
                                                    t={t}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>

                                <button
                                    type="button"
                                    onClick={addQuestion}
                                    className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 p-3 text-gray-600 transition-colors hover:border-black hover:text-black"
                                >
                                    <PlusIcon className="h-5 w-5" />
                                    <span>{t("Questionnaire.AddPage", "Add page")}</span>
                                </button>
                            </div>
                        </div>

                        {/* Centre — editor / live preview */}
                        <div className="flex-1 overflow-y-auto">
                            <div className="mx-auto max-w-2xl px-8 py-10">
                                {step === STEP_BASIC && (
                                    <div className="space-y-6">
                                        <h2 className="text-2xl font-light text-gray-900">
                                            {t("Questionnaire.BasicsHeading", "Let's start with the basics")}
                                        </h2>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                {t("Questionnaire.Title", "Title")} *
                                            </label>
                                            <input
                                                className={`mt-1 ${field}`}
                                                value={questionnaire.title || ""}
                                                onChange={(e) => patchQuestionnaire({ title: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                {t("Questionnaire.Description", "Description (optional)")}
                                            </label>
                                            <textarea
                                                rows={3}
                                                className={`mt-1 ${field}`}
                                                value={questionnaire.description || ""}
                                                onChange={(e) => patchQuestionnaire({ description: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                {t("Questionnaire.Language", "Language")}
                                            </label>
                                            <select
                                                className={`mt-1 ${field}`}
                                                value={questionnaire.language || "pt"}
                                                onChange={(e) => patchQuestionnaire({ language: e.target.value })}
                                            >
                                                {LANGUAGES.map((language) => (
                                                    <option key={language.value} value={language.value}>
                                                        {t(language.labelKey, language.fallback)}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="mt-1 text-xs text-gray-500">
                                                {t("Questionnaire.LanguageHelp", "The public form is shown to respondents in this language.")}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                {t("Questionnaire.Welcome", "Welcome message")}
                                            </label>
                                            <textarea
                                                rows={2}
                                                className={`mt-1 ${field}`}
                                                value={questionnaire.welcome_message || ""}
                                                onChange={(e) => patchQuestionnaire({ welcome_message: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                {t("Questionnaire.ThankYou", "Thank-you message")}
                                            </label>
                                            <textarea
                                                rows={2}
                                                className={`mt-1 ${field}`}
                                                value={questionnaire.thank_you_message || ""}
                                                onChange={(e) => patchQuestionnaire({ thank_you_message: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                )}

                                {step === STEP_CONSENT && (
                                    <div className="space-y-6">
                                        <div>
                                            <h2 className="text-2xl font-light text-gray-900">
                                                {t("Questionnaire.ConsentHeading", "What are respondents agreeing to?")}
                                            </h2>
                                            <p className="mt-2 text-sm text-gray-500">
                                                {t("Questionnaire.ConsentHelp", "Respondents must read and sign this before they can answer. Describe the study, what data you collect, and — if recording is on — that their screen and camera are recorded.")}
                                            </p>
                                        </div>
                                        <textarea
                                            rows={16}
                                            className={field}
                                            value={questionnaire.consent_content || ""}
                                            onChange={(e) => patchQuestionnaire({ consent_content: e.target.value })}
                                            placeholder={t("Questionnaire.ConsentPlaceholder", "Paste your consent text here…")}
                                        />
                                        <div className="flex items-center gap-3 text-sm">
                                            <label className="cursor-pointer font-medium text-amber-500 hover:underline">
                                                {t("Questionnaire.ConsentUpload", "or upload a PDF")}
                                                <input
                                                    type="file"
                                                    accept="application/pdf"
                                                    className="hidden"
                                                    onChange={handleDocumentUpload(uploadConsentDocument)}
                                                />
                                            </label>
                                            {questionnaire.consent_document_name && (
                                                <span className="text-gray-600">{questionnaire.consent_document_name}</span>
                                            )}
                                        </div>

                                        {/* Same page as consent, but optional: plenty of
                                            studies never go before a committee, so this
                                            never gates publishing. */}
                                        <div className="space-y-4 border-t pt-6">
                                            <div>
                                                <h3 className="text-lg font-light text-gray-900">
                                                    {t("Questionnaire.Ethics", "Ethics committee opinion")}
                                                    <span className="ml-2 align-middle text-sm text-gray-500">
                                                        {t("Questionnaire.EthicsOptional", "Optional")}
                                                    </span>
                                                </h3>
                                                <p className="mt-2 text-sm text-gray-500">
                                                    {t("Questionnaire.EthicsHelp", "Optional. Record the committee's opinion — its reference number, or the signed document — so respondents can see the study was approved. Leaving this empty does not stop you publishing.")}
                                                </p>
                                            </div>
                                            <textarea
                                                rows={6}
                                                className={field}
                                                value={questionnaire.ethics_content || ""}
                                                onChange={(e) => patchQuestionnaire({ ethics_content: e.target.value })}
                                                placeholder={t("Questionnaire.EthicsPlaceholder", "e.g. Opinion no. 123/2026, Ethics Committee of…")}
                                            />
                                            <div className="flex items-center gap-3 text-sm">
                                                <label className="cursor-pointer font-medium text-amber-500 hover:underline">
                                                    {t("Questionnaire.EthicsUpload", "or upload the opinion as a PDF")}
                                                    <input
                                                        type="file"
                                                        accept="application/pdf"
                                                        className="hidden"
                                                        onChange={handleDocumentUpload(uploadEthicsDocument)}
                                                    />
                                                </label>
                                                {questionnaire.ethics_document_name && (
                                                    <span className="text-gray-600">{questionnaire.ethics_document_name}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {step === "question" && currentQuestion && (
                                    <div className="space-y-6 text-left">
                                        {/* The question as the respondent sees it. */}
                                        <input
                                            type="text"
                                            value={currentQuestion.question_text || ""}
                                            onChange={(e) => patchQuestion({ question_text: e.target.value })}
                                            placeholder={t("Questionnaire.PlaceholderQuestion", "Enter your question here…")}
                                            className="w-full border-0 bg-transparent p-0 text-3xl font-light leading-tight text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
                                            maxLength={200}
                                        />
                                        <input
                                            type="text"
                                            value={currentQuestion.description || ""}
                                            onChange={(e) => patchQuestion({ description: e.target.value })}
                                            placeholder={t("Questionnaire.PlaceholderDescription", "Description (optional)")}
                                            className="w-full border-0 bg-transparent p-0 text-base italic text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-0"
                                            maxLength={500}
                                        />

                                        {/* Live preview: the exact component the public page renders. */}
                                        <div className="pt-2">
                                            <QuestionInput
                                                question={currentQuestion}
                                                value={undefined}
                                                onChange={() => {}}
                                                disabled
                                                preview
                                                placeholder={t("Questionnaire.PreviewAnswer", "Type your answer here…")}
                                            />
                                        </div>

                                        {CHOICE_TYPES.includes(currentQuestion.type) && (
                                            <div className="pt-6">
                                                <div className="mb-3 flex items-center justify-between">
                                                    <h3 className="text-sm font-medium text-gray-700">
                                                        {t("Questionnaire.Options", "Options")}
                                                    </h3>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            patchQuestion({
                                                                options: [...(currentQuestion.options || []), ""],
                                                            })
                                                        }
                                                        className="inline-flex items-center gap-1.5 rounded-md bg-black px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800"
                                                    >
                                                        <PlusIcon className="h-4 w-4" />
                                                        {t("Questionnaire.AddOption", "Add option")}
                                                    </button>
                                                </div>

                                                {(currentQuestion.options || []).length === 0 ? (
                                                    <div className="rounded-lg border-2 border-dashed border-gray-300 py-10 text-center">
                                                        <p className="text-sm text-gray-500">
                                                            {t("Questionnaire.NoOptions", "No options added yet")}
                                                        </p>
                                                        <button
                                                            type="button"
                                                            onClick={() => patchQuestion({ options: [""] })}
                                                            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
                                                        >
                                                            <PlusIcon className="h-4 w-4" />
                                                            {t("Questionnaire.AddFirstOption", "Add first option")}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-2">
                                                        {(currentQuestion.options || []).map((option, position) => (
                                                            <div key={position} className="flex items-center gap-2">
                                                                <input
                                                                    className="flex-1 rounded-md border-gray-300 text-sm shadow-sm focus:border-black focus:ring-black"
                                                                    value={option}
                                                                    placeholder={`${t("Questionnaire.Option", "Option")} ${position + 1}`}
                                                                    onChange={(e) => {
                                                                        const options = [...currentQuestion.options];
                                                                        options[position] = e.target.value;
                                                                        patchQuestion({ options });
                                                                    }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        patchQuestion({
                                                                            options: currentQuestion.options.filter(
                                                                                (_, i) => i !== position
                                                                            ),
                                                                        })
                                                                    }
                                                                    className="text-gray-400 hover:text-red-600"
                                                                >
                                                                    <TrashIcon className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right — settings for the current step */}
                        <div className="w-80 flex-none overflow-y-auto border-l bg-gray-50 p-6">
                            {step === "question" && currentQuestion ? (
                                <div className="space-y-5">
                                    <h3 className="text-sm font-semibold text-gray-900">
                                        {t("Questionnaire.QuestionSettings", "Question settings")}
                                    </h3>

                                    <div>
                                        <label className="block text-sm text-gray-700">
                                            {t("Questionnaire.QuestionType", "Question type")}
                                        </label>
                                        <select
                                            className={`mt-1 ${field}`}
                                            value={currentQuestion.type}
                                            onChange={(e) => {
                                                const type = e.target.value;
                                                patchQuestion({
                                                    type,
                                                    options: CHOICE_TYPES.includes(type)
                                                        ? currentQuestion.options || []
                                                        : null,
                                                });
                                            }}
                                        >
                                            {QUESTION_TYPES.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {t(type.labelKey, type.fallback)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {currentQuestion.type === "rating" && (
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1">
                                                <label className="block text-sm text-gray-700">
                                                    {t("Questionnaire.ScaleMin", "From")}
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`mt-1 ${field}`}
                                                    value={currentQuestion.min_value ?? 1}
                                                    onChange={(e) => patchQuestion({ min_value: Number(e.target.value) })}
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <label className="block text-sm text-gray-700">
                                                    {t("Questionnaire.ScaleMax", "To")}
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`mt-1 ${field}`}
                                                    value={currentQuestion.max_value ?? 5}
                                                    onChange={(e) => patchQuestion({ max_value: Number(e.target.value) })}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between border-t pt-4">
                                        <span className="text-sm text-gray-700">
                                            {t("Questionnaire.RequiredQuestion", "Required question")}
                                        </span>
                                        <input
                                            type="checkbox"
                                            checked={!!currentQuestion.required}
                                            onChange={(e) => patchQuestion({ required: e.target.checked })}
                                            className="rounded text-black focus:ring-black"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <span className="flex items-center gap-1 text-sm text-gray-700">
                                            {t("Questionnaire.UseAsSubjectProperty", "Use as subject property")}
                                            <span className="group relative inline-flex">
                                                <InformationCircleIcon
                                                    className="h-4 w-4 cursor-help text-gray-400"
                                                    tabIndex={0}
                                                    aria-label={t(
                                                        "Questionnaire.SubjectPropertyHint",
                                                        "A subject property is an attribute stored on each participant's profile (e.g. age, role, department). Answers to this question are saved to the respondent's subject, so you can filter and compare participants by it across the project."
                                                    )}
                                                />
                                                <span
                                                    role="tooltip"
                                                    className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-64 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal text-white shadow-lg group-hover:block group-focus-within:block"
                                                >
                                                    {t(
                                                        "Questionnaire.SubjectPropertyHint",
                                                        "A subject property is an attribute stored on each participant's profile (e.g. age, role, department). Answers to this question are saved to the respondent's subject, so you can filter and compare participants by it across the project."
                                                    )}
                                                </span>
                                            </span>
                                        </span>
                                        <input
                                            type="checkbox"
                                            checked={!!currentQuestion.use_as_subject_property}
                                            onChange={(e) => patchQuestion({ use_as_subject_property: e.target.checked })}
                                            className="rounded text-black focus:ring-black"
                                        />
                                    </div>

                                    <button
                                        type="button"
                                        onClick={removeQuestion}
                                        className="flex w-full items-center justify-center gap-2 rounded-md bg-red-50 py-2.5 text-sm font-medium text-red-600 hover:bg-red-100"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                        {t("Questionnaire.DeleteQuestion", "Delete question")}
                                    </button>
                                </div>
                            ) : step === STEP_CONSENT ? (
                                <div className="space-y-4">
                                    <h3 className="text-sm font-semibold text-gray-900">
                                        {t("Questionnaire.Consent", "Informed consent")}
                                    </h3>
                                    <div
                                        className={`flex items-start gap-2 rounded-md p-3 text-xs ${
                                            hasConsent ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800"
                                        }`}
                                    >
                                        <ShieldCheckIcon className="mt-0.5 h-4 w-4 flex-none" />
                                        <span>
                                            {hasConsent
                                                ? t("Questionnaire.ConsentOk", "Respondents will sign this before answering.")
                                                : t("Questionnaire.ConsentMissing", "Add informed consent text before publishing.")}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        {t("Questionnaire.ConsentSnapshotNote", "The exact text is stored with each response at the moment it is signed, so later edits never change what past respondents agreed to.")}
                                    </p>
                                    <div
                                        className={`flex items-start gap-2 rounded-md p-3 text-xs ${
                                            hasEthics ? "bg-green-50 text-green-800" : "bg-gray-100 text-gray-700"
                                        }`}
                                    >
                                        <DocumentCheckIcon className="mt-0.5 h-4 w-4 flex-none" />
                                        <span>
                                            {hasEthics
                                                ? t("Questionnaire.EthicsOk", "Respondents will see the ethics opinion beside the consent form.")
                                                : t("Questionnaire.EthicsNone", "No ethics committee opinion — optional, you can publish without it.")}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <h3 className="text-sm font-semibold text-gray-900">
                                        {t("Questionnaire.Settings", "Settings")}
                                    </h3>
                                    {[
                                        ["collect_email", t("Questionnaire.CollectEmail", "Ask for an email address")],
                                        ["allow_multiple_responses", t("Questionnaire.AllowMultiple", "Allow repeat responses")],
                                        // Screen/camera recording temporarily disabled — re-enable here and in PublicQuestionnaire.js (wantsRecording).
                                        // ["record_screen", t("Questionnaire.RecordScreen", "Record screen")],
                                        // ["record_camera", t("Questionnaire.RecordCamera", "Record camera")],
                                    ].map(([key, label]) => (
                                        <div key={key} className="flex items-center justify-between">
                                            <span className="pr-3 text-sm text-gray-700">{label}</span>
                                            <input
                                                type="checkbox"
                                                checked={!!questionnaire[key]}
                                                onChange={(e) => patchQuestionnaire({ [key]: e.target.checked })}
                                                className="flex-none rounded text-black focus:ring-black"
                                            />
                                        </div>
                                    ))}
                                    {/* Screen/camera recording temporarily disabled.
                                    {(questionnaire.record_screen || questionnaire.record_camera) && (
                                        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                            {t("Questionnaire.RecordingConsentNote", "Recording is on — say so in the consent text. Respondents are asked for permission only after signing.")}
                                        </p>
                                    )}
                                    */}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t bg-white p-6">
                        <div className="flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                {t("Generic.Cancel", "Cancel")}
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    clearTimeout(saveTimer.current);
                                    await persistSettings(questionnaire);
                                    if (currentQuestion) await persistQuestion(currentQuestion);
                                    onClose();
                                }}
                                className="rounded-md bg-black px-5 py-2 text-sm font-medium text-white hover:bg-gray-800"
                            >
                                {t("Questionnaire.UpdateQuestionnaire", "Update questionnaire")}
                            </button>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
